import React, { useRef, useEffect, useState } from 'react';

/**
 * iOS-style Wheel Picker Component
 * Creates a scrollable list with snap-to-center behavior
 */
export default function WheelPicker({ options, value, onChange, label }) {
  const containerRef = useRef(null);
  const itemRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [itemHeight, setItemHeight] = useState(36);

  // Measure actual item height from DOM (responsive)
  useEffect(() => {
    if (itemRef.current) {
      const height = itemRef.current.offsetHeight;
      if (height > 0) setItemHeight(height);
    }
  }, []);

  // Find the index of the currently selected value
  const selectedIndex = options.findIndex(opt => opt === value);

  // Scroll to the selected item on mount or when value changes
  useEffect(() => {
    if (containerRef.current && selectedIndex >= 0) {
      const scrollPosition = selectedIndex * itemHeight;
      containerRef.current.scrollTop = scrollPosition;
    }
  }, [value, selectedIndex, itemHeight]);

  // Handle scroll events to update selection
  const handleScroll = () => {
    if (!containerRef.current || isDragging) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
    
    if (options[clampedIndex] !== value) {
      onChange(options[clampedIndex]);
    }
  };

  // Snap to nearest item when scrolling stops
  const handleScrollEnd = () => {
    setIsDragging(false);
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
    const targetScroll = clampedIndex * itemHeight;
    
    containerRef.current.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
    
    onChange(options[clampedIndex]);
  };

  // Handle click on an option
  const handleOptionClick = (option, index) => {
    onChange(option);
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: index * itemHeight,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="wheel-picker-wrapper">
      {label && <div className="wheel-picker-label">{label}:</div>}
      <div className="wheel-picker-container">
        {/* Highlight bar for selected item */}
        <div className="wheel-picker-highlight"></div>
        
        {/* Scrollable list */}
        <div
          ref={containerRef}
          className="wheel-picker-scroll"
          onScroll={handleScroll}
          onScrollCapture={() => setIsDragging(true)}
          onMouseUp={handleScrollEnd}
          onTouchEnd={handleScrollEnd}
        >
          {/* Top padding to center first item */}
          <div className="wheel-picker-item" style={{ visibility: 'hidden' }}>
            &nbsp;
          </div>
          
          {/* Options */}
          {options.map((option, index) => (
            <div
              key={index}
              ref={index === 0 ? itemRef : null}
              className={`wheel-picker-item ${option === value ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option, index)}
            >
              {option}
            </div>
          ))}
          
          {/* Bottom padding to center last item */}
          <div className="wheel-picker-item" style={{ visibility: 'hidden' }}>
            &nbsp;
          </div>
        </div>
      </div>
    </div>
  );
}
